import { ReactNode } from 'react';
import { Box } from '@mui/material';

interface ScenarioFormRowProps {
    children: ReactNode;
}

/** How wide the whole metadata form block may get (CALL-784).
 *
 *  Two columns plus the 16px gap, so a single field lands at (840 - 16) / 2 = 412px. Left to the page width
 *  each field measured 736px — three times what a date or a version name needs — and the seven of them
 *  pushed the grid that holds the actual data to y=563 on an 827px viewport, i.e. below the fold. The board
 *  fits the same fields in a ~215px column with the label to its left; this keeps MUI's floating labels
 *  (which every other form in the app uses) and just stops the block from spanning the screen. */
const SCENARIO_FORM_MAX_WIDTH = 840;

// One row of the scenario form: two equal columns on md and up, stacked below it.
//
// Deliberately a CSS grid rather than MUI's `<Grid container spacing={2}>`. A Grid container implements
// its gutter as `margin-left: -16px; width: calc(100% + 16px)`, which only cancels out when its parent is
// a Grid item carrying the matching padding. Here the rows are children of a plain Stack, so every one of
// them hung 16px past the left edge of the page block and overflowed the same amount on the right — the
// misalignment the form was reported for. `gap` has no negative margins, so the rows line up with the
// headings and grids around them, and a single-field row (Type, Scope) occupies the first column at the
// same width as the two-field rows above it.
//
// The width cap lives HERE, on the row, rather than on the form that composes them: the bucket/version
// pair and the scope multiselect are rows too, from two other files, and they have to line up with the
// metadata fields above them.
const ScenarioFormRow = ({ children }: ScenarioFormRowProps) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            alignItems: 'start',
            maxWidth: SCENARIO_FORM_MAX_WIDTH,
        }}
    >
        {children}
    </Box>
);

export default ScenarioFormRow;
